<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RecycleScroller } from 'vue-virtual-scroller'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { DeviceFile } from '@lib/types/files'
import type { Upload } from '@browser/stores/upload.types'

interface Props {
  uploads: Upload[]
}

const props = withDefaults(defineProps<Props>(), {
  uploads: Array,
})

defineEmits(['clear', 'remove'])

const themeVars = useThemeVars()
const { t } = useI18n()

const MESSAGE_TRUNCATE_LENGTH = 35
const NAME_TRUNCATE_LENGTH = 15
const isShowingContent = ref(true)
const truncate = (fileName: string, length = 20, keepExtension = false) => {
  if (fileName.length > length) {
    return `${fileName.substring(0, length)}...${keepExtension ? fileName.split('.').pop() : ''}`
  }

  return fileName
}

const uploadingCount = computed(
  () => props.uploads.filter((u) => u.uploadStatus === 'transferring' || u.uploadStatus === 'pending').length,
)
const completedCount = computed(() => props.uploads.filter((u) => u.uploadStatus === 'completed').length)
const isTransferring = computed(() => props.uploads.some((u) => u.uploadStatus === 'transferring'))
</script>

<template>
  <CCard v-if="uploads.length" :bordered="false" class="flex flex-col gap-2 w-[500px] mr-4 upload-files-widget">
    <div class="flex justify-between items-center pa-4 upload-files-widget__header">
      <div class="upload-files-widget__header-title">
        {{
          isTransferring
            ? t('canvasUI.browserApp.uploadFilesWidget.uploadingCountTitle', {
                count: uploadingCount,
              })
            : t('canvasUI.browserApp.uploadFilesWidget.uploadedCountTitle', {
                count: completedCount,
              })
        }}
      </div>
      <CButton circle secondary @click="isShowingContent = !isShowingContent">
        <template #icon>
          <CIcon :color="themeVars.primary" :icon="`angle-${isShowingContent ? 'down' : 'up'}`" />
        </template>
      </CButton>
    </div>

    <template v-if="isShowingContent">
      <div class="flex justify-end pa-4 border-b upload-files-widget__actions">
        <CButton
          :disabled="!uploads.filter((u) => ['completed', 'failed'].includes(u.uploadStatus)).length"
          quaternary
          @click="$emit('clear')"
        >
          {{ t('canvasUI.common.clear') }}
        </CButton>
      </div>

      <RecycleScroller
        v-slot="{ item: upload }: { item: Upload }"
        :itemSize="52"
        :items="uploads"
        keyField="id"
        :style="{ maxHeight: `${52 * 4}px` }"
      >
        <div :key="upload.id" class="flex justify-between items-center px-4 py-2 upload-files-widget__list-item">
          <div class="flex flex-1 items-center gap-2 upload-files-widget__list-item-content">
            <div class="font-bold text-truncate flex-shrink-0 upload-files-widget__list-item-name">
              {{ truncate((upload.content as DeviceFile)?.name ?? '', NAME_TRUNCATE_LENGTH, true) }}
            </div>
            <div class="flex justify-end items-center text-truncate w-[59%] upload-files-widget__list-item-status">
              <span v-if="upload.uploadStatus === 'pending'">
                {{ truncate(t('canvasUI.browserApp.uploadFilesWidget.status.pending'), MESSAGE_TRUNCATE_LENGTH) }}
              </span>
              <span v-else-if="upload.uploadStatus === 'failed' && upload.error" class="text-error">
                {{ truncate(upload.error, MESSAGE_TRUNCATE_LENGTH) }}
              </span>
              <span v-else-if="upload.uploadStatus === 'transferring'">
                {{ truncate(t('canvasUI.browserApp.uploadFilesWidget.status.transferring'), MESSAGE_TRUNCATE_LENGTH) }}
              </span>
              <span v-else-if="upload.uploadStatus === 'completed'">100%</span>
            </div>
            <!-- <template #append> -->
            <div class="flex flex-1 justify-end items-center gap-2 ml-4 upload-files-widget__list-item-actions">
              <template v-if="upload.uploadStatus === 'transferring'">
                <CSpin class="mr-1" size="small" />

                <CButton circle :disabled="true">
                  <template #icon>
                    <CIcon icon="cancel" />
                  </template>
                </CButton>
              </template>

              <CButton
                v-else-if="upload.uploadStatus === 'completed'"
                circle
                class="cursor-auto"
                :color="themeVars.success2"
              >
                <template #icon>
                  <CIcon :color="themeVars.success" icon="check" />
                </template>
              </CButton>

              <CButton
                v-else-if="upload.uploadStatus === 'failed'"
                circle
                class="cursor-auto"
                :color="themeVars.error2"
              >
                <template #icon>
                  <CIcon :color="themeVars.error" icon="exclamation" />
                </template>
              </CButton>

              <CButton
                v-if="['failed', 'completed', 'pending'].includes(upload.uploadStatus)"
                circle
                secondary
                @click="$emit('remove', upload.id)"
              >
                <template #icon>
                  <CIcon icon="close" />
                </template>
              </CButton>
            </div>
          </div>
        </div>
      </RecycleScroller>
    </template>
  </CCard>
</template>

<style lang="scss" scoped>
.upload-files-widget {
  position: fixed;
  bottom: 0;
  right: 0;
  border-radius: 10px 10px 0 0;

  :deep(.n-spin) {
    color: v-bind('themeVars.success');
  }

  :deep(.n-card__content) {
    padding: 0;
  }

  &__header {
    background: v-bind('themeVars.primary');
    border-radius: 10px 10px 0 0;

    &-title {
      color: v-bind('themeVars.base');
      font-size: 20px;
    }
  }
  &__actions {
    border-bottom: 1px solid v-bind('themeVars.primary6');
  }

  &__list-item {
    &:hover {
      background: v-bind('themeVars.primary6');
    }

    &-status {
      color: v-bind('themeVars.text2');
      font-size: 12px;
    }
  }
}
</style>
